/**
 * Pure mapping from boxed-backend AssetGroupDdb → catalog_item (+ external ids).
 * Used by the migrate-catalog script; no I/O, no outbox.
 */

import { gameFamilyOfTags, gameOfTags } from "@loot-os/api-contract";
import { CatalogItemStatus, Circulation } from "@loot-os/core/db/schema";

import type { ExternalIdSystem } from "./external-id";
import type { CatalogItem, FulfillableStatus, PricingSource } from "./item";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when pk is a catalog UUID (TCG / CUSTOM_TCG rows eligible for FS catalog). */
export function isBoxedCatalogUuid(pk: string): boolean {
  return UUID_RE.test(pk);
}

/** TCG set name from boxed assets[0], or null when absent or blank. */
export function readBoxedAssetCollectionName(
  group: Pick<BoxedAssetGroup, "assets">,
): string | null {
  const name = group.assets?.[0]?.collectionName?.trim();
  return name && name.length > 0 ? name : null;
}

const FULFILLABLE_STATUSES = new Set<FulfillableStatus>([
  "NO_RISK",
  "MEDIUM_RISK",
  "HIGH_RISK",
  "UNFULFILLABLE",
  "SUPER_UNFULFILLABLE",
  "ARCHIVED",
]);

/** Minimal boxed AssetGroup shape the mapper understands. */
export interface BoxedAssetGroup {
  pk: string;
  name?: string;
  protocol?: string;
  quantity?: number;
  withdrawingCount?: number;
  fulfillableStatus?: string;
  tags?: string[];
  overrideImage?: string;
  createdAt?: string;
  updatedAt?: string;
  filter?: Record<string, unknown>;
  inventoryDetails?: {
    listingCount?: number;
    overrideFulfillableStatus?: string;
    userInventoryCount?: number;
    rocaId?: string;
    /** USD dollars (TCG scrape). */
    marketPrice?: number;
  };
  assets?: Array<{
    id?: string;
    name?: string;
    image?: string;
    collectionName?: string;
    /** Boxed's gems were cents numerically, so the value is stored as-is. */
    value?: number;
    price?: number;
    compareAtValue?: number;
    compareAtLabelOverride?: string;
    details?: Record<string, unknown>;
    protocol?: string;
  }>;
}

export interface MappedExternalId {
  system: ExternalIdSystem;
  value: string;
}

export type MapBoxedCatalogResult =
  | {
      ok: true;
      item: CatalogItem;
      externalIds: MappedExternalId[];
    }
  | { ok: false; skip: string; pk?: string };

function asStatus(value: string | undefined, fallback: FulfillableStatus): FulfillableStatus {
  if (value && FULFILLABLE_STATUSES.has(value as FulfillableStatus)) {
    return value as FulfillableStatus;
  }
  return fallback;
}

function parseDate(value: string | undefined, fallback: Date): Date {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : d;
}

/** Boxed inventoryDetails.marketPrice is USD dollars → FS market_price cents. */
export function marketPriceDollarsToCents(marketPrice: number | undefined): number | null {
  if (marketPrice == null || !Number.isFinite(marketPrice)) return null;
  return Math.round(marketPrice * 100);
}

function pricingSourceForProtocol(protocol: string): PricingSource {
  if (protocol === "TCG") return "TCGPLAYER";
  return "MANUAL";
}

function buildPricingRef(
  protocol: string,
  filter: Record<string, unknown> | undefined,
  assetId: string | undefined,
): Record<string, unknown> | null {
  if (protocol !== "TCG") return null;
  const productId =
    (typeof filter?.productId === "string" && filter.productId) ||
    (typeof assetId === "string" && assetId) ||
    undefined;
  if (!productId) return null;
  const printing = typeof filter?.printing === "string" ? filter.printing : undefined;
  return printing ? { productId, printing } : { productId };
}

/**
 * Category and subcategory for a boxed TCG import, from its site-category tags.
 * The leaf tag is the subcategory (`POKEMON`, `BASEBALL`). A sports card is category
 * `SPORTS`; every other game this import accepts stays `TCG`. A leaf that is the
 * category itself (a bare `SPORTS` tag) leaves subcategory empty.
 */
export function taxonomyFromBoxedTags(tags: string[] | undefined): {
  category: "TCG" | "SPORTS";
  subcategory: string | null;
} {
  const leaf = gameOfTags(tags);
  const category = gameFamilyOfTags(tags) === "SPORTS" ? "SPORTS" : "TCG";
  return { category, subcategory: leaf && leaf !== category ? leaf : null };
}

function collectExternalIds(input: {
  protocol: string;
  assetId: string | undefined;
  filter: Record<string, unknown> | undefined;
  rocaId: string | undefined;
}): MappedExternalId[] {
  const out: MappedExternalId[] = [];
  if (input.rocaId?.trim()) {
    out.push({ system: "ROCA", value: input.rocaId.trim() });
  }
  const productId =
    (typeof input.filter?.productId === "string" && input.filter.productId) ||
    (input.protocol === "TCG" && input.assetId) ||
    undefined;
  if (typeof productId === "string" && productId.trim()) {
    out.push({ system: "TCGPLAYER", value: productId.trim() });
  }
  return out;
}

/**
 * Map one boxed AssetGroup to a CatalogItem. Only TCG / CUSTOM_TCG are accepted.
 * Preserves `pk` as `id` when it is a UUID.
 */
export function mapBoxedAssetGroupToCatalog(
  group: BoxedAssetGroup,
  now: Date = new Date(),
): MapBoxedCatalogResult {
  const protocol = group.protocol;
  if (protocol !== "TCG" && protocol !== "CUSTOM_TCG") {
    return { ok: false, skip: "UNSUPPORTED_PROTOCOL", pk: group.pk };
  }
  if (!isBoxedCatalogUuid(group.pk)) {
    return { ok: false, skip: "NON_UUID_PK", pk: group.pk };
  }

  const asset = group.assets?.[0];
  const name = (group.name || asset?.name || "").trim();
  if (!name) {
    return { ok: false, skip: "MISSING_NAME", pk: group.pk };
  }

  const filter = group.filter;
  const inv = group.inventoryDetails;
  const pricingRef = buildPricingRef(protocol, filter, asset?.id);

  const collectionName = readBoxedAssetCollectionName(group);
  const fulfillableStatus = asStatus(group.fulfillableStatus, "UNFULFILLABLE");
  const overrideFulfillableStatus =
    inv?.overrideFulfillableStatus &&
    FULFILLABLE_STATUSES.has(inv.overrideFulfillableStatus as FulfillableStatus)
      ? (inv.overrideFulfillableStatus as FulfillableStatus)
      : null;
  // Boxed archives through the fulfillable status; Loot OS keeps it as the lifecycle status.
  const archived = fulfillableStatus === "ARCHIVED" || overrideFulfillableStatus === "ARCHIVED";

  const item: CatalogItem = {
    id: group.pk,
    name,
    image: asset?.image ?? null,
    overrideImage: group.overrideImage ?? null,
    sourceId: asset?.id ?? null,
    // Boxed's gems were cents; stored as cents, no rescale.
    value: asset?.value ?? asset?.price ?? null,
    marketPrice: marketPriceDollarsToCents(inv?.marketPrice),
    compareAtValue: asset?.compareAtValue ?? null,
    compareAtLabelOverride: asset?.compareAtLabelOverride ?? null,
    pricingSource: pricingSourceForProtocol(protocol),
    pricingRef,
    // TCG = DYNAMIC (source as needed); CUSTOM_TCG = FIXED (require on-hand).
    availabilityStrategy: protocol === "TCG" ? "DYNAMIC" : "FIXED",
    quantity: group.quantity ?? 0,
    withdrawingCount: group.withdrawingCount ?? 0,
    listingCount: inv?.listingCount ?? null,
    userInventoryCount: inv?.userInventoryCount ?? null,
    fulfillableStatus: archived ? "ARCHIVED" : fulfillableStatus,
    overrideFulfillableStatus,
    status: archived ? CatalogItemStatus.ARCHIVED : CatalogItemStatus.STANDARD,
    circulation: Circulation.OPEN,
    overrideCirculation: null,
    autoArchive: false,
    ...taxonomyFromBoxedTags(group.tags),
    type: null,
    tags: group.tags ?? [],
    attributes: {
      boxedProtocol: protocol,
      filter: filter ?? null,
      details: asset?.details ?? null,
      ...(collectionName ? { collectionName } : {}),
    },
    isInForge: false,
    isInMarketplace: false,
    tenantId: null,
    createdAt: parseDate(group.createdAt, now),
    updatedAt: parseDate(group.updatedAt, now),
  };

  return {
    ok: true,
    item,
    externalIds: collectExternalIds({
      protocol,
      assetId: asset?.id,
      filter,
      rocaId: inv?.rocaId,
    }),
  };
}
